import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Badge, Spinner, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faRobot, 
  faBrain, 
  faGem, 
  faCheck, 
  faExclamationTriangle,
  faCog,
  faChevronDown,
  faStar
} from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import apiClient from '../../services/api';
import { toast } from 'react-toastify';
import './AIProviderSelector.css';

// Función auxiliar para obtener modelos por proveedor (función pura)
const getProviderModels = (provider) => {
  const modelsList = {
    openai: [
      { 
        id: 'gpt-4o-mini', 
        name: 'GPT-4o Mini', 
        description: 'Rápido y económico',
        tier: 'standard',
        icon: faRobot
      },
      { 
        id: 'gpt-4o', 
        name: 'GPT-4o', 
        description: 'Más potente y preciso',
        tier: 'premium',
        icon: faStar
      },
      { 
        id: 'gpt-4', 
        name: 'GPT-4', 
        description: 'Clásico y confiable',
        tier: 'standard',
        icon: faRobot
      }
    ],
    anthropic: [
      { 
        id: 'claude-3-5-sonnet-20241022', 
        name: 'Claude 3.5 Sonnet', 
        description: 'Último modelo, máxima precisión',
        tier: 'premium',
        icon: faStar
      },
      { 
        id: 'claude-3-5-haiku-20241022', 
        name: 'Claude 3.5 Haiku', 
        description: 'Rápido y eficiente',
        tier: 'standard',
        icon: faBrain
      },
      { 
        id: 'claude-3-opus-20240229', 
        name: 'Claude 3 Opus', 
        description: 'Máxima capacidad creativa',
        tier: 'premium',
        icon: faStar
      }
    ],
    gemini: [
      { 
        id: 'gemini-pro', 
        name: 'Gemini Pro', 
        description: 'Modelo principal multimodal',
        tier: 'standard',
        icon: faGem
      }
    ]
  };
  
  return modelsList[provider] || [];
};

const AIProviderSelector = ({ onProviderChange, onModelChange, currentProvider, currentModel }) => {
  const [providers, setProviders] = useState([]);
  const [models, setModels] = useState({});
  const [loading, setLoading] = useState(false);
  const [health, setHealth] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Configuración chevere de proveedores con gradientes y animaciones (inspirado en el template)
  const providerConfig = {
    openai: {
      name: 'OpenAI GPT',
      icon: faRobot,
      gradient: 'linear-gradient(135deg, #74D680 0%, #378CE7 100%)',
      color: '#378CE7',
      bgClass: 'bg-soft-primary',
      description: 'ChatGPT avanzado, rápido y versátil',
      features: ['Respuestas rápidas', 'Análisis profundo', 'Creatividad alta']
    },
    anthropic: {
      name: 'Anthropic Claude',
      icon: faBrain,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#667eea',
      bgClass: 'bg-soft-secondary',
      description: 'Claude 3.5 con análisis superior y precisión',
      features: ['Máxima precisión', 'Análisis detallado', 'Ética avanzada']
    },
    gemini: {
      name: 'Google Gemini',
      icon: faGem,
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      color: '#f093fb',
      bgClass: 'bg-soft-info',
      description: 'Gemini Pro multimodal y potente',
      features: ['Multimodal', 'Visión avanzada', 'Integración Google']
    }
  };

  const loadAvailableProviders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/chat/diagnostics');
      
      if (response.data.success) {
        const availableProviders = Object.keys(response.data.data.providers || {});
        setProviders(availableProviders);
        
        // Cargar modelos para cada proveedor
        const modelsData = {};
        availableProviders.forEach(provider => {
          modelsData[provider] = getProviderModels(provider);
        });
        setModels(modelsData);
      }
    } catch (error) {
      console.error('Error loading providers:', error);
      toast.error('Error al cargar proveedores de IA');
    } finally {
      setLoading(false);
    }
  }, []);

  const checkProvidersHealth = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/chat-config/providers/health');
      if (response.data.success) {
        setHealth(response.data.data);
      }
    } catch (error) {
      console.error('Error checking provider health:', error);
    }
  }, []);

  // Cargar configuración inicial
  useEffect(() => {
    loadAvailableProviders();
    checkProvidersHealth();
    // Auto-refresh health cada 30 segundos
    const interval = setInterval(checkProvidersHealth, 30000);
    return () => clearInterval(interval);
  }, [loadAvailableProviders, checkProvidersHealth]);



  const handleProviderSelect = async (provider) => {
    try {
      // Actualizar configuración en la base de datos
      await apiClient.put('/api/chat-config/ai_default_provider', {
        config_value: provider
      });
      
      // Seleccionar primer modelo disponible para el proveedor
      const providerModels = models[provider] || [];
      const defaultModel = providerModels[0]?.id;
      
      if (defaultModel) {
        await apiClient.put('/api/chat-config/ai_default_model', {
          config_value: defaultModel
        });
        onModelChange?.(defaultModel);
      }
      
      onProviderChange?.(provider);
      
      toast.success(`🚀 Cambiado a ${providerConfig[provider]?.name || provider}`, {
        position: 'top-right',
        theme: 'colored'
      });
    } catch (error) {
      console.error('Error updating provider:', error);
      toast.error('Error al cambiar proveedor');
    }
  };

  const handleModelSelect = async (model) => {
    try {
      await apiClient.put('/api/chat-config/ai_default_model', {
        config_value: model
      });
      
      onModelChange?.(model);
      
      const modelInfo = models[currentProvider]?.find(m => m.id === model);
      toast.success(`✨ Modelo cambiado a ${modelInfo?.name || model}`, {
        position: 'top-right',
        theme: 'colored'
      });
    } catch (error) {
      console.error('Error updating model:', error);
      toast.error('Error al cambiar modelo');
    }
  };

  const getCurrentProviderConfig = () => {
    return providerConfig[currentProvider] || {
      name: currentProvider,
      icon: faRobot,
      color: '#6c757d',
      bgClass: 'bg-soft-secondary',
      description: 'Proveedor personalizado'
    };
  };

  const getCurrentModel = () => {
    const providerModels = models[currentProvider] || [];
    return providerModels.find(m => m.id === currentModel) || {
      name: currentModel,
      description: 'Modelo actual'
    };
  };

  const isProviderHealthy = (provider) => {
    return health[provider] === true;
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm ai-selector-loading">
        <Card.Body className="text-center py-4">
          <Spinner animation="border" variant="primary" className="mb-2" />
          <div className="text-muted">Conectando con las IAs...</div>
        </Card.Body>
      </Card>
    );
  }

  const currentConfig = getCurrentProviderConfig();
  const currentModelInfo = getCurrentModel();

  return (
    <div className="galaxy-ai-selector">
      {/* Header Principal */}
      <Card className="border-0 shadow-sm mb-3 ai-selector-card">
        <Card.Body className="px-4 py-3">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div 
                className="ai-provider-avatar me-3"
                style={{ background: currentConfig.gradient }}
              >
                <FontAwesomeIcon 
                  icon={currentConfig.icon} 
                  className="text-white" 
                  size="lg"
                />
              </div>
              <div>
                <h6 className="mb-1 fw-bold">
                  {currentConfig.name}
                  {isProviderHealthy(currentProvider) ? (
                    <Badge bg="success" className="ms-2 pulse-badge">
                      <FontAwesomeIcon icon={faCheck} size="xs" className="me-1" />
                      Activo
                    </Badge>
                  ) : (
                    <Badge bg="warning" className="ms-2">
                      <FontAwesomeIcon icon={faExclamationTriangle} size="xs" className="me-1" />
                      Verificando
                    </Badge>
                  )}
                </h6>
                <small className="text-muted">
                  {currentModelInfo.name} • {currentConfig.description}
                </small>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="ai-toggle-btn btn-galaxy-primary"
            >
              <FontAwesomeIcon 
                icon={faCog} 
                className={classNames('me-1 transition-all', {
                  'fa-spin': showAdvanced
                })} 
              />
              {showAdvanced ? 'Ocultar' : 'Cambiar IA'}
              <FontAwesomeIcon 
                icon={faChevronDown} 
                className={classNames('ms-1 transition-transform', {
                  'rotate-180': showAdvanced
                })} 
                size="sm"
              />
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Panel Avanzado de Selección */}
      {showAdvanced && (
        <Card className="border-0 shadow-sm ai-advanced-panel">
          <Card.Header className="bg-soft-primary">
            <h6 className="mb-0 text-primary">
              <FontAwesomeIcon icon={faStar} className="me-2" />
              Seleccionar Asistente de IA
            </h6>
          </Card.Header>
          <Card.Body className="p-4">
            {/* Selector de Proveedores */}
            <Row className="g-3 mb-4">
              {providers.map((provider) => {
                const config = providerConfig[provider];
                const healthy = isProviderHealthy(provider);
                const isActive = currentProvider === provider;
                
                return (
                  <Col md={4} key={provider}>
                    <div
                      className={classNames('ai-provider-card h-100 cursor-pointer transition-all', {
                        'active': isActive,
                        'unhealthy': !healthy
                      })}
                      onClick={() => handleProviderSelect(provider)}
                      style={{ 
                        '--provider-color': config?.color,
                        '--provider-gradient': config?.gradient 
                      }}
                    >
                      <div className="ai-provider-card-header">
                        <div 
                          className="ai-provider-icon"
                          style={{ background: config?.gradient }}
                        >
                          <FontAwesomeIcon 
                            icon={config?.icon || faRobot} 
                            className="text-white" 
                          />
                        </div>
                        {healthy ? (
                          <Badge bg="success" className="ai-status-badge pulse-badge">
                            <FontAwesomeIcon icon={faCheck} size="xs" />
                          </Badge>
                        ) : (
                          <Badge bg="danger" className="ai-status-badge">
                            !
                          </Badge>
                        )}
                      </div>
                      <div className="ai-provider-card-body">
                        <h6 className="fw-bold mb-1">{config?.name || provider}</h6>
                        <p className="small text-muted mb-2">{config?.description}</p>
                        <div className="ai-provider-features">
                          {config?.features?.map((feature, index) => (
                            <Badge key={index} bg="light" text="dark" className="me-1 mb-1">
                              {feature}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      {isActive && (
                        <div className="ai-provider-selected">
                          <FontAwesomeIcon icon={faCheck} className="text-white" />
                        </div>
                      )}
                    </div>
                  </Col>
                );
              })}
            </Row>

            {/* Selector de Modelos */}
            {currentProvider && models[currentProvider]?.length > 0 && (
              <div className="ai-model-selector">
                <h6 className="text-muted mb-3">
                  <FontAwesomeIcon icon={faStar} className="me-2" />
                  Modelos disponibles para {currentConfig.name}
                </h6>
                <Row className="g-2">
                  {models[currentProvider].map((model) => {
                    const isActive = currentModel === model.id;
                    
                    return (
                      <Col md={6} lg={4} key={model.id}>
                        <div
                          className={classNames('ai-model-card cursor-pointer transition-all', {
                            'active': isActive
                          })}
                          onClick={() => handleModelSelect(model.id)}
                        >
                          <div className="d-flex align-items-center">
                            <div className="ai-model-icon me-2">
                              <FontAwesomeIcon 
                                icon={model.icon} 
                                className={classNames({
                                  'text-warning': model.tier === 'premium',
                                  'text-primary': model.tier === 'standard'
                                })}
                              />
                            </div>
                            <div className="flex-grow-1">
                              <div className="fw-bold small">{model.name}</div>
                              <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                {model.description}
                              </div>
                            </div>
                            {model.tier === 'premium' && (
                              <Badge bg="warning" className="ms-2">
                                <FontAwesomeIcon icon={faStar} size="xs" />
                              </Badge>
                            )}
                            {isActive && (
                              <Badge bg="success" className="ms-2">
                                <FontAwesomeIcon icon={faCheck} size="xs" />
                              </Badge>
                            )}
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </div>
            )}

            {/* Acciones */}
            <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={checkProvidersHealth}
              >
                <FontAwesomeIcon icon={faCog} className="me-1" />
                Verificar Estado
              </Button>
              <Button
                variant="soft-primary"
                size="sm"
                onClick={() => setShowAdvanced(false)}
              >
                <FontAwesomeIcon icon={faCheck} className="me-1" />
                Listo
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default AIProviderSelector; 